import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Chip, Stack, Tooltip, Typography } from "@mui/material";
import AddCardOutlinedIcon from "@mui/icons-material/AddCardOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { fetchSupplierBillPipeline } from "../../../../../services/reports";
import FinanceSectionShell from "./FinanceSectionShell";

const money = (value, currency = "CAD") => Number(value).toLocaleString(undefined, {
  style: "currency",
  currency,
  minimumFractionDigits: 2,
});

export const getBillsToEnterStatus = (row = {}) => {
  if (row.billingState === "Partial" || row.isPartiallyBilled) return "Partial";
  if (row.billingState === "Billed" || row.isFullyBilled) return "Billed";
  return "No Bill";
};

export const formatBillsToEnterAge = (days) => {
  const value = Number(days);
  if (!Number.isFinite(value) || value < 0) return "—";
  if (value === 0) return "Today";
  return `${value} ${value === 1 ? "day" : "days"}`;
};

export const buildBillsToEnterUrl = (instanceUniqueId, row = {}) => {
  if (!instanceUniqueId || !row.orderId) return null;
  const base = `/instance/${encodeURIComponent(instanceUniqueId)}/sales/orders/${encodeURIComponent(row.orderId)}/bills`;
  const query = new URLSearchParams();
  if (!row.hasBill && row.poNumber) {
    query.set("action", "add");
    query.set("poNumber", row.poNumber);
  }
  return query.toString() ? `${base}?${query}` : base;
};

export const buildSupplierBillPipelineUrl = (instanceUniqueId) =>
  `/instance/${encodeURIComponent(instanceUniqueId)}/reports/supplier-bill-pipeline`;

const statusChipSx = (label) => label === "Partial"
  ? { color: "#92400e", backgroundColor: "#fffbeb", borderColor: "#fcd34d" }
  : label === "Billed"
    ? { color: "#166534", backgroundColor: "#f0fdf4", borderColor: "#86efac" }
    : { color: "#1e40af", backgroundColor: "#eff6ff", borderColor: "#93c5fd" };

const ageSx = (days) => {
  const value = Number(days);
  if (value >= 30) return { color: "error.main", fontWeight: 800 };
  if (value >= 14) return { color: "warning.dark", fontWeight: 750 };
  return { color: "text.secondary", fontWeight: 650 };
};

export function FinanceBillsToEnterView({ instanceUniqueId, limit = 12, state, onRetry }) {
  return (
    <FinanceSectionShell
      title="Bills to Enter"
      subtitle="Supplier POs that may require bill entry or follow-up."
      countLabel={`${state.summary?.purchaseOrderCount ?? state.rows.length} to review`}
      loading={state.loading}
      error={state.error}
      onRetry={onRetry}
      empty={!state.rows.length}
      emptyTitle="You're caught up"
      emptyBody="No supplier bills currently require attention."
      viewAllHref={buildSupplierBillPipelineUrl(instanceUniqueId)}
      viewAllLabel="View Supplier Bill Pipeline"
    >
      <Box sx={{ display: { xs: "none", md: "grid" }, gridTemplateColumns: "120px minmax(150px,1.2fr) minmax(140px,1fr) 150px 120px 105px 85px 110px", gap: 1.25, px: 2.5, py: 1, backgroundColor: "#fafbfc", borderBottom: 1, borderColor: "divider" }}>
        {["PO", "Project", "Supplier", "PO Status", "Expected", "Bill Status", "Age", "Action"].map((label) => (
          <Typography key={label} variant="caption" color="text.secondary" fontWeight={800}>{label}</Typography>
        ))}
      </Box>
      <Stack divider={<Box sx={{ borderTop: 1, borderColor: "divider" }} />}>
        {state.rows.slice(0, limit).map((row, index) => {
          const billStatus = getBillsToEnterStatus(row);
          const billUrl = buildBillsToEnterUrl(instanceUniqueId, row);
          const actionLabel = row.hasBill ? "View Bills" : "Add Bill";
          const expected = row.expectedCost == null ? "—" : money(row.expectedCost, row.currency || "CAD");
          return (
            <Box key={row.purchaseOrderId || `${row.poNumber}-${index}`} sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5, display: "grid", gridTemplateColumns: { xs: "minmax(0,1fr) auto", md: "120px minmax(150px,1.2fr) minmax(140px,1fr) 150px 120px 105px 85px 110px" }, gap: 1.25, alignItems: "center" }}>
              <Button component="a" href={row.purchaseOrderId ? `/generated-purchase-order/${encodeURIComponent(row.purchaseOrderId)}` : undefined} target="_blank" rel="noopener noreferrer" disabled={!row.purchaseOrderId} size="small" sx={{ justifyContent: "flex-start", textTransform: "none", fontWeight: 800, px: 0 }}>{row.poNumber || "—"}</Button>
              <Box sx={{ minWidth: 0 }}><Typography variant="body2" fontWeight={750} noWrap>{row.projectName || `Project ${row.orderId || "—"}`}</Typography><Typography variant="caption" color="text.secondary">Order {row.orderId || "—"}</Typography></Box>
              <Typography variant="body2" noWrap sx={{ display: { xs: "none", md: "block" } }}>{row.supplierName || "Unknown Supplier"}</Typography>
              <Chip size="small" variant="outlined" label={row.supplierStatus || "—"} sx={{ display: { xs: "none", md: "inline-flex" }, justifySelf: "start", fontWeight: 700 }} />
              <Tooltip title={row.expectedCost == null ? "No authoritative PO billing target is available." : row.remainingBillAmount != null ? `${money(row.remainingBillAmount, row.currency || "CAD")} remaining` : "Expected PO billing target"} arrow>
                <Typography variant="body2" fontWeight={750} sx={{ display: { xs: "none", md: "block" } }}>{expected}</Typography>
              </Tooltip>
              <Chip size="small" variant="outlined" label={billStatus} sx={{ display: { xs: "none", md: "inline-flex" }, justifySelf: "start", fontWeight: 750, ...statusChipSx(billStatus) }} />
              <Typography variant="caption" sx={{ display: { xs: "none", md: "block" }, ...ageSx(row.daysOutstanding) }}>{formatBillsToEnterAge(row.daysOutstanding)}</Typography>
              <Button component="a" href={billUrl || undefined} target="_blank" rel="noopener noreferrer" disabled={!billUrl} size="small" variant={row.hasBill ? "outlined" : "contained"} startIcon={row.hasBill ? <ReceiptLongOutlinedIcon /> : <AddCardOutlinedIcon />} sx={{ textTransform: "none", whiteSpace: "nowrap" }}>{actionLabel}</Button>
            </Box>
          );
        })}
      </Stack>
    </FinanceSectionShell>
  );
}

export default function FinanceBillsToEnter({ instanceUniqueId, limit = 12 }) {
  const [state, setState] = useState({ loading: true, error: "", rows: [], summary: {} });
  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const response = await fetchSupplierBillPipeline({
        instanceUniqueId,
        view: "actionable",
        page: 1,
        pageSize: limit,
        sortBy: "priority",
      });
      setState({
        loading: false,
        error: "",
        rows: Array.isArray(response?.rows) ? response.rows : [],
        summary: response?.summary || {},
      });
    } catch (_) {
      setState({ loading: false, error: "We couldn't load supplier bills that need attention.", rows: [], summary: {} });
    }
  }, [instanceUniqueId, limit]);

  useEffect(() => { load(); }, [load]);
  return <FinanceBillsToEnterView instanceUniqueId={instanceUniqueId} limit={limit} state={state} onRetry={load} />;
}
